import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from './ErrorContext';
import { useUser } from './UserContext';
import { useClient } from './ClientContext';
import api from '../api';

const DataContext = createContext();

export const DataProvider = ({ children }) => {
    const { loading, setLoading } = useLoading();
    const { setError } = useError();
    const { user } = useUser();
    const { client } = useClient();

    // high level data
    const [categories, setCategories] = useState([]);
    const [clothesOptions, setClothesOptions] = useState([]);
    const [profileOptions, setProfileOptions] = useState([]);
    const [tags, setTags] = useState([]);

    // client data
    const [items, setItems] = useState([]);
    const [outfits, setOutfits] = useState([]);
    const [shopping, setShopping] = useState([]);

    // closet states
    const [currentCategory, setCurrentCategory] = useState({ _id: -1, name: 'All', type: 'clothes' });
    const [currentTab, setCurrentTab] = useState('clothes');
    const [currentItems, setCurrentItems] = useState([]);

    const resetData = useCallback(() => {
        setItems([]);
        setOutfits([]);
        setShopping([]);
        setCurrentCategory({ _id: -1, name: 'All', type: 'clothes' });
        setCurrentTab('clothes');
        setCurrentItems([]);
        setLoading(false);
    }, [setLoading]);

    const toggleCurrentTab = useCallback(() => {
        if (currentTab === 'profile') {
            setCurrentTab('clothes');
        }
        else {
            setCurrentTab('profile');
        }
    }, [currentTab]);

    // === categories ---------------------------------------------
    const updateCategories = useCallback(async () => {
        try {
            const response = await api.get('/categories');
            const categories = response.data.sort((a, b) => {
                if (a.name < b.name) {
                    return -1;
                }
                else if (a.name > b.name) {
                    return 1;
                }
                else {
                    return 0;
                }
            });
            setCategories(categories);
        }
        catch (err) {
            setError({
                message: 'There was an error fetching categories.',
                status: err?.response?.status,
            });
        }
    }, [setError]);

    const getCategoryName = useCallback((categoryId) => {
        const category = categories.filter(category => category._id === categoryId);
        let categoryName = '';
        if (category.length) {
            categoryName = category[0]?.name;
        }
        return categoryName;
    }, [categories]);

    const getCategoryType = useCallback((categoryId) => {
        const category = categories.filter(category => category._id === categoryId);
        let categoryType = 'clothes';
        if (category.length && (category[0]?.type === 'profile')) {
            categoryType = 'profile';
        }
        return categoryType;
    }, [categories]);

    const getCategoryPermissions = useCallback((categoryId) => {
        const category = categories.filter(category => category._id === categoryId)[0];
        return {
            clientViewItems: Boolean(category?.clientViewItems),
            clientAddItems: Boolean(category?.clientAddItems),
            rmbgItems: Boolean(category?.rmbgItems),
        };
    }, [categories]);

    const getCategoryDropdownOptions = useCallback((type) => {
        let theseCategories = categories.filter(category => category._id !== 0);
        if (!user?.isSuperAdmin && !user?.isAdmin) {
            theseCategories = theseCategories.filter(category => category.clientViewItems && category.clientAddItems);
        }
        if (type === 'profile') {
            theseCategories = theseCategories.filter(category => category.type === 'profile');
        }
        else {
            theseCategories = theseCategories.filter(category => category.type !== 'profile');
        }

        const categoriesWithGroups = theseCategories.filter(category => category.group);
        const categoriesWithoutGroups = theseCategories.filter(category => !category.group);

        const groupMap = {};
        for (const category of categoriesWithGroups) {
            if (!groupMap[category.group]) {
                groupMap[category.group] = [];
            }
            groupMap[category.group].push(category);
        }
        for (const category of categoriesWithoutGroups) {
            if (!groupMap['Other']) {
                groupMap['Other'] = [];
            }
            groupMap['Other'].push(category);
        }

        if (type !== 'profile') {
            if (!groupMap['Other']) {
                groupMap['Other'] = [];
            }
            groupMap['Other'].unshift({ _id: 0, name: 'Other' });
        }

        const groups = Object.keys(groupMap).sort((a, b) => {
            if (a === 'Other' && b === 'Other') {
                return 0;
            }
            else if (a === 'Other' && b !== 'Other') {
                return 1;
            }
            else if (a !== 'Other' && b === 'Other') {
                return -1;
            }
            else if (a < b) { 
                return -1; 
            }
            else if (a > b) { 
                return 1; 
            }
            else { 
                return 0; 
            }
        });

        const options = [];
        for (const group of groups) {
            const categoryOptions = [];
            const groupCategories = groupMap[group];
            for (const category of groupCategories) {
                categoryOptions.push({
                    value: category._id,
                    label: category.name,
                });
            }
            options.push({
                type: 'group',
                name: group,
                items: categoryOptions,
            });
        }

        return options;
    }, [user, categories]);

    useEffect(() => {
        const clothes = getCategoryDropdownOptions('clothes');
        const profile = getCategoryDropdownOptions('profile');

        setClothesOptions(clothes);
        setProfileOptions(profile);
    }, [categories, getCategoryDropdownOptions]);

    // === tags ---------------------------------------------
    const updateTags = useCallback(async () => {
        try {
            const response = await api.get('/tags/active');
            const tagData = response.data;

            tagData.forEach(tagGroup => {
                tagGroup.tags.sort(function(a, b) {
                    if (a.tagName < b.tagName) {
                        return -1;
                    }
                    else if (a.tagName > b.tagName) {
                        return 1;
                    }
                    else {
                        return 0;
                    }
                });
            });

            tagData.sort(function(a, b) {
                if (a?.sortOrder === undefined) return 1;
                if (b?.sortOrder === undefined) return -1;

                return a.sortOrder - b.sortOrder;
            });
        
            setTags(tagData);
        } 
        catch (err) {
            setError({
                message: 'There was an error fetching tags.',
                status: err?.response?.status,
            });
        }
    }, [setError]);

    const resolveTagIds = useCallback((tagIds) => {
        const tagsUsed = [];
        tags.forEach(group => {
            group.tags.forEach(tag => {
                if (tagIds?.includes(tag.tagId)) {
                    tagsUsed.push(tag);
                }
            });
        });
        return tagsUsed;

    }, [tags]);

    // === items ---------------------------------------------
    const getLinkItemUrl = useCallback((url) => {
        if (!url?.startsWith('https://docs.google.com')) {
            return '';
        }
        else {
            let path = url?.split('?')[0];
            path = path.replace('https://', '');

            let partsToRemove = ['e', 'edit', 'preview', 'pub', 'pubembed'];
            let parts = path?.split('/');
            parts = parts.filter(part => !partsToRemove?.includes(part));

            return 'https://' + parts.join('/');
        }
    }, []);

    const updateItems = useCallback(async () => {
        if (client) {
            try {
                const response = await api.get(`/items/${client._id}`);

                const theseItems = [];
                for (const item of response.data) {
                    const tags = resolveTagIds(item.tags).map(tag => tag.tagName);
                    item.tagNamesPrefix = tags.join(' | ');
                    item.categoryName = getCategoryName(item.categoryId);
                    item.categoryType = getCategoryType(item.categoryId);
                    if (item.type === 'link') {
                        item.urlToDisplay = getLinkItemUrl(item.url);
                    }
                    theseItems.push(item);
                }
                theseItems.sort((a, b) => {
                    const nameA = a?.tagNamesPrefix ? a.tagNamesPrefix + ' | ' + a.fileName : a.fileName;
                    const nameB = b?.tagNamesPrefix ? b.tagNamesPrefix + ' | ' + b.fileName : b.fileName;
                    if (nameA < nameB) {
                        return -1;
                    }
                    else if (nameA > nameB) {
                        return 1;
                    }
                    else {
                        return 0;
                    }
                });
                setItems(theseItems);
            }
            catch (err) {
                setError({
                    message: 'There was an error fetching the client\'s items.',
                    status: err?.response?.status,
                });
            }
        }
    }, [client, resolveTagIds, getCategoryName, getCategoryType, getLinkItemUrl, setError]);

    // current category's items
    useEffect(() => {
        const itemsToUse = items.filter(item => item.categoryType === currentCategory.type);
        if (currentCategory._id === -1) {
            setCurrentItems(itemsToUse);
        }
        else {
            setCurrentItems(itemsToUse.filter(item => item.categoryId === currentCategory._id));
        }
    }, [items, currentCategory]);

    // === outfits ---------------------------------------------
    const updateOutfits = useCallback(async () => {
        if (client) {
            try {
                const response = await api.get(`/outfits/${client._id}`);

                // reverse outfits to show recently created first
                setOutfits(response?.data?.reverse());
            }
            catch (err) {
                setError({
                    message: 'There was an error fetching the client\'s outfits.',
                    status: err?.response?.status,
                });
            }
        }
    }, [client, setError]);

    // === shopping ---------------------------------------------
    const updateShopping = useCallback(async () => {
        if (client) {
            try {
                const response = await api.get(`/shopping/${client._id}`);
                setShopping(response.data);
            } 
            catch (err) {
                setError({
                    message: 'There was an error fetching client shopping items.',
                    status: err.response.status
                });
            }
        }
    }, [client, setError]);

    // === updates on user/client change ---------------------------------------------
    useEffect(() => {
        if (user) {
            updateCategories();
            updateTags();
        }
    }, [user, updateCategories, updateTags]);

    const updateAll = useCallback(async () => {
        setLoading(true);
        await updateItems();
        await updateOutfits();
        await updateShopping();
        setLoading(false);
    }, [updateItems, updateOutfits, updateShopping, setLoading]);

    useEffect(() => {
        if (client) {
            updateAll();
        }
    }, [client, updateAll]);

    return (
        <DataContext.Provider value={{  
            categories,
            clothesOptions,
            profileOptions,
            updateCategories,
            getCategoryPermissions,

            tags,
            updateTags,
            resolveTagIds,

            updateAll, 
            items,
            updateItems,
            outfits,
            updateOutfits,
            shopping,
            updateShopping,

            currentCategory,
            setCurrentCategory,
            currentTab,
            toggleCurrentTab,
            currentItems,
            setCurrentItems,
            
            loading,
            setLoading,

            resetData,
        }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    return useContext(DataContext);
}